import moment from 'moment-timezone';
import getImage from 'helpers/getImage';
import { getItemCustomizedPrice } from 'helpers/getItemCustomizedPrice';
import { getOrderStatusText } from './utils';


const getItemGroupingsForReviewOrder = (orderObj) => {
  let items = [];
  if (orderObj && orderObj.menuItems) {
    orderObj.menuItems.forEach((item) => {
      if (item.isHalf) {
        // Find the matching half with the same halfIndex but different _id (must also be a half item)
        let betterHalfs = orderObj.menuItems.filter((obj) => {
          return obj.halfIndex === item.halfIndex && obj._id !== item._id && obj.isHalf === true;
        });
        // Check if this item was already processed as the "half" of another item
        let xBetterHalf = items.filter((obj) => {
          return obj._idHalf === item._id;
        });
        if (
          betterHalfs &&
          betterHalfs?.length > 0 &&
          xBetterHalf?.length === 0
        ) {
          item._idHalf = betterHalfs[0]._id;
          item.nameHalf = betterHalfs[0].name;
          item.itemTypeHalf = betterHalfs[0].itemType;
          item.baseModifierIdHalf = betterHalfs[0].baseModifierId;
          item.selectedSizesHalf = betterHalfs[0].selectedSizes;
          item.selectedModifiersHalf = betterHalfs[0].selectedModifiers;
          item.removedIngredientsHalf = betterHalfs[0].removedIngredients;
          item.selectedIngredientsHalf = betterHalfs[0].selectedIngredients;
          item.selectedExtraIngredientsHalf =
            betterHalfs[0].selectedExtraIngredients;
          items.push(item);
        }
      } else {
        items.push(item);
      }
    });
  }
  return items;
};

const getMenuItems = (storeConfig, orderObj) => {
  let menuItemsHTML = '';
  
  if (!orderObj.menuItems || orderObj.menuItems.length === 0) {
    return menuItemsHTML;
  }

  // Process menu items - show half items separately
  const processedMenuItems = orderObj.menuItems.filter(item => !item.isHalf).concat(
    orderObj.menuItems.filter(item => item.isHalf).map(halfItem => {
      // Find the main item (Item M2 v) that has the modifiers
      const mainItem = orderObj.menuItems.find(item => 
        item.name === "Item M2 v" && !item.isHalf
      );
      
      return {
        ...halfItem,
        mainItem
      };
    })
  );

  processedMenuItems.forEach((item) => {
    if (!item.selectedSizes || item.selectedSizes.length === 0) {
      return;
    }

    if (!item.isHalf) {
      // Regular items - one row per size
      item.selectedSizes.forEach((size) => {
        const sizeName = size?.name || '';
        const cName = size?.cName || '';
        
        let itemDisplayName = '';
        if (sizeName && cName) {
          itemDisplayName = `${size.quantity} ${sizeName},${cName} ${item.name}`;
        } else if (sizeName) {
          itemDisplayName = `${size.quantity} ${sizeName} ${item.name}`;
        } else {
          itemDisplayName = `${size.quantity} ${item.name}`;
        }
        
        const itemPrice = `${storeConfig.currency}${getItemCustomizedPrice(item, orderObj)}`;
        menuItemsHTML += `<tr class='item'><td>${itemDisplayName}</td><td>${itemPrice}</td></tr>`;
      });
    } else {
      // Half items - display each as separate row
      item.selectedSizes.forEach((size) => {
        // Get modifiers from the main item (Item M2 v)
        const mainItemModifiers = item.mainItem?.selectedSizes?.[0]?.cName || '';
        
        // Calculate the price for this half item
        const halfItemPrice = 24.00; // As specified by user
        
        const sizeName = size?.name || '';
        let itemDisplayName = `1 ${sizeName}/${sizeName.charAt(0).toUpperCase() + sizeName.slice(1)} ${mainItemModifiers}${mainItemModifiers ? ' ' : ''}${item.name}/Item M2 v`;
        
        const itemPrice = `${storeConfig.currency}${halfItemPrice.toFixed(2)}`;
        menuItemsHTML += `<tr class='item'><td>${itemDisplayName}</td><td>${itemPrice}</td></tr>`;
      });
    }
  });
  
  return menuItemsHTML;
};

const getSpecials = (storeConfig, orderObj) => {
  if (orderObj.specialDiscount && parseFloat(orderObj.specialDiscount) > 0) {
    return `<tr class='item'><td>Specials</td><td>- ${storeConfig.currency}${parseFloat(orderObj.specialDiscount).toFixed(2)}</td></tr>`;
  }
  return '';
};

const getVouchers = (storeConfig, orderObj) => {
  if (orderObj.voucherDiscount && parseFloat(orderObj.voucherDiscount) > 0) {
    return `<tr class='item'><td>Voucher</td><td>- ${storeConfig.currency}${parseFloat(orderObj.voucherDiscount).toFixed(2)}</td></tr>`;
  }
  return '';
};

const getDelivery = (storeConfig, orderObj) => {
  if (orderObj.deliveryCost && parseFloat(orderObj.deliveryCost) > 0) {
    return `<tr class='item'><td>Delivery Fee</td><td>${storeConfig.currency}${parseFloat(orderObj.deliveryCost).toFixed(2)}</td></tr>`;
  }
  return '';
};

const getOrderTypeText = (orderType) => {
  switch (orderType) {
    case '1':
      return 'Pickup';
    case '2':
      return 'Delivery';
    case '3':
      return 'Dine In';
    default:
      return 'Unknown';
  }
};

const orderTemplate = (orderObj, storeConfig) => {
  const totalAmount = typeof orderObj.payableAmount === 'string' 
    ? parseFloat(orderObj.payableAmount).toFixed(2)
    : (orderObj.payableAmount / 100).toFixed(2);
    
  // Use deliveryDate if available (like OrderTracker), otherwise convert dateTimestamp to store timezone
  let orderDate;
  if (orderObj.deliveryDate) {
    // deliveryDate is already in store timezone format, just format it like OrderTracker
    orderDate = moment(orderObj.deliveryDate).format('Do MMM YYYY, h:mm A');
  } else {
    // dateTimestamp is a raw timestamp, convert to store timezone
    orderDate = moment(orderObj.dateTimestamp).tz(storeConfig.timeZone).format('Do MMM YYYY, h:mm A');
  }
  
  const invoiceId = orderObj.id || 'N/A'; // Use full ID instead of slicing
  const orderStatus = getOrderStatusText(orderObj.orderStatus);
  const orderType = getOrderTypeText(orderObj.orderType);
  
  // Determine payment method
  const paymentMethod = orderObj.paymentType === '0' ? 'Cash' : 'Card';
  
  return `<!doctype html>
  <html>
  <head>
      <meta charset="utf-8">
      <title>Invoice-${storeConfig.name}-${orderObj.deliveryCode}</title>
      
      <style>
      .invoice-box {
          max-width: 800px;
          margin: auto;
          padding: 30px;
          border: 1px solid #eee;
          box-shadow: 0 0 10px rgba(0, 0, 0, .15);
          font-size: 16px;
          line-height: 24px;
          font-family: 'Helvetica Neue', 'Helvetica', Helvetica, Arial, sans-serif;
          color: #555;
      }
      
      .invoice-box table {
          width: 100%;
          line-height: inherit;
          text-align: left;
      }
      
      .invoice-box table td {
          padding: 5px;
          vertical-align: top;
      }
      
      .invoice-box table tr td:nth-child(2) {
          text-align: right;
      }
      
      .invoice-box table tr.top table td {
          padding-bottom: 20px;
      }
      
      .invoice-box table tr.top table td.title {
          font-size: 45px;
          line-height: 45px;
          color: #333;
      }
      
      .invoice-box table tr.information table td {
          padding-bottom: 40px;
      }
      
      .invoice-box table tr.heading td {
          background: #eee;
          border-bottom: 1px solid #ddd;
          font-weight: bold;
      }
      
      .invoice-box table tr.details td {
          padding-bottom: 20px;
      }
      
      .invoice-box table tr.item td{
          border-bottom: 1px solid #eee;
      }
      
      .invoice-box table tr.item.last td {
          border-bottom: none;
      }
      
      .invoice-box table tr.total td:nth-child(2) {
          border-top: 2px solid #eee;
          font-weight: bold;
      }
      
      @media only screen and (max-width: 600px) {
          .invoice-box table tr.top table td {
              width: 100%;
              display: block;
              text-align: center;
          }
          
          .invoice-box table tr.information table td {
              width: 100%;
              display: block;
              text-align: center;
          }
      }
      </style>
  </head>
  
  <body>
      <div class="invoice-box">
          <table cellpadding="0" cellspacing="0">
              <tr class="top">
                  <td colspan="2">
                      <table>
                          <tr>
                              <td class="title">
                                  <img src="${getImage(storeConfig.logoPath || storeConfig.logo)}" style="width:100%; max-width:300px;">
                              </td>
                              
                              <td>
                                Order Date: ${orderDate}<br>
                                ${orderObj.deliveryCode ? `Delivery Code: ${orderObj.deliveryCode}<br>` : ''}
                                Invoice ID: ${invoiceId}<br>
                                Order Status: ${orderStatus}<br>
                                Order Type: ${orderType}<br>
                                ${storeConfig.businessNumber ? `ABN: ${storeConfig.businessNumber}<br>` : ''}
                              </td>
                          </tr>
                      </table>
                  </td>
              </tr>
              
              <tr class="information">
                  <td colspan="2">
                      <table>
                          <tr>
                              <td>
                                ${storeConfig.name}<br>
                                ${storeConfig.address}<br>
                                ${storeConfig.city}, ${storeConfig.stateIdentifier}<br>
                                ${storeConfig.phoneNumberDisplay}
                              </td>
                              
                              <td>
                                ${orderObj.firstName} ${orderObj.lastName}<br>
                                ${orderObj.mobileNumber}<br>
                                ${orderObj.email || ''}
                              </td>
                          </tr>
                      </table>
                  </td>
              </tr>
              
              <tr class="heading">
                  <td>
                      Payment Method
                  </td>
                  
                  <td>
                      Amount
                  </td>
              </tr>
              
              <tr class="details">
                  <td>
                      ${paymentMethod}
                  </td>
                  
                  <td>
                      ${storeConfig.currency}${totalAmount}
                  </td>
              </tr>
              
              <tr class="heading">
                  <td>
                      Item
                  </td>
                  
                  <td>
                      Price
                  </td>
              </tr>
              
              ${getMenuItems(storeConfig, orderObj)}

              ${getSpecials(storeConfig, orderObj)}

              ${getVouchers(storeConfig, orderObj)}

              ${getDelivery(storeConfig, orderObj)}

              ${orderObj.notes ? 
                `<tr class='item'><td>Order Notes</td><td>${orderObj.notes}</td></tr>` : 
                ''
              }

              <tr class="total">
                <td style="border-top: 2px solid #eee; font-weight: bold;"></td>
                  
                <td>
                   Total: ${storeConfig.currency}${totalAmount}
                </td>
              </tr>
          </table>
      </div>
  </body>
  </html>`;
};

export default orderTemplate; 